import io from "socket.io-client";
import api from "../services/api";

class SocketWorker {
  constructor(companyId, userId) {
    const sessionToken = api.defaults.headers.Authorization;

    if (!SocketWorker.instance) {
      this.companyId = companyId || "";
      this.userId = userId || "";
      this.token = sessionToken || "";
      this.socket = null;
      this.eventListeners = {};
      this.configureSocket();
      SocketWorker.instance = this;
      return this;
    }

    SocketWorker.instance.updateSession(companyId, userId, sessionToken);
    return SocketWorker.instance;
  }

  hasConnectionChanged(companyId, userId, token) {
    return (
      String(this.companyId || "") !== String(companyId || "") ||
      String(this.userId || "") !== String(userId || "") ||
      String(this.token || "") !== String(token || "")
    );
  }

  updateSession(companyId, userId, token) {
    const nextCompanyId = companyId || "";
    const nextUserId = userId || "";
    const nextToken = token || "";

    if (!this.hasConnectionChanged(nextCompanyId, nextUserId, nextToken)) {
      this.connect();
      return;
    }

    const registeredListeners = this.eventListeners;

    if (this.socket) {
      Object.entries(registeredListeners).forEach(([event, callbacks]) => {
        callbacks.forEach(callback => this.socket.off(event, callback));
      });
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.companyId = nextCompanyId;
    this.userId = nextUserId;
    this.token = nextToken;
    this.eventListeners = registeredListeners;
    this.configureSocket();

    if (this.socket) {
      Object.entries(this.eventListeners).forEach(([event, callbacks]) => {
        callbacks.forEach(callback => this.socket.on(event, callback));
      });
    }
  }

  configureSocket() {
    if (!process.env.REACT_APP_BACKEND_URL || !this.companyId || !this.token) {
      return;
    }

    this.socket = io(`${process.env.REACT_APP_BACKEND_URL}/${this.companyId}`, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      transports: ["websocket"],
      query: { userId: this.userId, token: this.token }
    });

    this.socket.on("connect", () => {
      console.log("Conectado ao servidor Socket.IO");
      this._authFailed = false;
    });

    this.socket.on("AUTH_ERROR", (data) => {
      console.warn("Socket auth error:", data?.message);
      this._authFailed = true;
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
      }
    });

    this.socket.on("disconnect", (reason) => {
      console.log("Desconectado do servidor Socket.IO", reason);
      if (reason !== "io client disconnect" && !this._authFailed) {
        this.reconnectAfterDelay();
      }
    });
  }

  on(event, callback) {
    if (typeof callback !== "function") {
      return;
    }

    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }

    if (this.eventListeners[event].includes(callback)) {
      return;
    }

    this.eventListeners[event].push(callback);
    this.connect();

    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  emit(event, data) {
    this.connect();
    if (this.socket && this.socket.connected) {
      this.socket.emit(event, data);
    } else if (this.socket) {
      // Queue emit to fire once connected
      const onceConnect = () => {
        this.socket.emit(event, data);
        this.socket.off("connect", onceConnect);
      };
      this.socket.on("connect", onceConnect);
    }
  }

  off(event, callback) {
    if (!this.eventListeners[event]) {
      return;
    }

    if (callback) {
      if (this.socket) {
        this.socket.off(event, callback);
      }
      this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
      if (this.eventListeners[event].length === 0) {
        delete this.eventListeners[event];
      }
      return;
    }

    if (this.socket) {
      this.eventListeners[event].forEach(cb => this.socket.off(event, cb));
    }
    delete this.eventListeners[event];
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.eventListeners = {};
    SocketWorker.instance = null;
    console.log("Socket desconectado manualmente");
  }

  reconnectAfterDelay() {
    setTimeout(() => {
      if (!this.socket || !this.socket.connected) {
        console.log("Tentando reconectar após desconexão");
        this.connect();
      }
    }, 1000);
  }

  connect() {
    if (!this.companyId || !this.token) {
      return;
    }

    if (!this.socket) {
      this.configureSocket();
      // Re-attach saved listeners to the new socket
      if (this.socket) {
        Object.entries(this.eventListeners).forEach(([event, callbacks]) => {
          callbacks.forEach(callback => this.socket.on(event, callback));
        });
      }
      return;
    }

    if (!this.socket.connected) {
      this.socket.connect();
    }
  }

  forceReconnect() {
    this.updateSession(this.companyId, this.userId, this.token);
  }
}

const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
